import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Stack,
  alpha,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  Notes as NotesIcon,
  DeleteOutline as DeleteOutlineIcon,
} from "@mui/icons-material";
import { format, formatDistanceToNowStrict } from "date-fns";
import { tr } from "date-fns/locale";

import ActionButton from "../../atoms/ActionButton";
import SelectableListRow from "../../molecules/SelectableListRow";
import { RowLine, RowEditorLine, RowMetaText } from "./_rowParts";
import ConfirmDialog from "../../common/ConfirmDialog";
import AnamnesisEditor from "./AnamnesisEditor";
import {
  LIST_ROW_SX,
  WORKSPACE_ROOT_SX,
  WorkspaceBody,
  WorkspaceSplit,
  WorkspaceSkeleton,
  WorkspaceEmpty,
  SelectPrompt,
  CreatingRow,
} from "./_workspaceParts";
import { useClinicTheme } from "../../../theme";
import { COLORS } from "../../../styles/uiConstants";
import {
  readWorkspaceState,
  writeWorkspaceState,
} from "../../../pages/Examination/ExaminationSession/workspaceStorage";
import {
  useListAnamnezByPatientMutation,
  useDeleteAnamnezMutation,
} from "../../../store/secretaryApi";
import useTerms from "../../../hooks/useTerms";

const diagnosisLabel = (d) =>
  d ? d.diagnosisName || d.diagnosisCategoryName || "Tanı belirtilmemiş" : "—";

const formatCreatedAt = (value) => {
  if (!value) return "—";
  try {
    return format(new Date(value), "d MMM yyyy", { locale: tr });
  } catch (_) {
    return "—";
  }
};

const ROLE_LABEL = {
  Admin: "Yönetici",
  AdminStaff: "Admin",
  ClinicStaff: "Klinik",
};

const formatEditor = (user) => {
  if (!user) return null;
  const title = user.title ? `${user.title} ` : "";
  const full = `${title}${user.name || ""} ${user.surname || ""}`.trim();
  return {
    name: full || "—",
    role: ROLE_LABEL[user.role] || user.role || "",
  };
};

const formatRelativeTr = (value) => {
  if (!value) return "";
  try {
    return formatDistanceToNowStrict(new Date(value), {
      addSuffix: true,
      locale: tr,
    });
  } catch (_) {
    return "";
  }
};

/**
 * AnamnesisTab — master-detail inline edit.
 *
 * @property {"standalone" | "session"} mode
 * @property {string} patientId
 * @property {string} token
 * @property {boolean} [canEdit=true]
 * @property {object} [initialData]  Session: { list, examinationId }
 */
export default function AnamnesisTab({
  mode,
  patient,
  patientId,
  token,
  canEdit = true,
  initialData,
  registerAutoSave,
  registerFlush,
  onSaveStatusChange,
  onRecordChanged,
}) {
  const terms = useTerms();
  const { palette } = useClinicTheme();

  // Local (section-internal) alert. Global toast yerine bu kullanılıyor —
  // anamnez akışıyla ilgili mesaj burada görünür, dışarıdaki toast'a düşmez.
  const [localAlert, setLocalAlert] = useState({
    open: false,
    severity: "success",
    message: "",
  });
  const alertTimerRef = useRef(null);
  const showLocal = useCallback((message, severity = "success", duration = 3500) => {
    if (alertTimerRef.current) clearTimeout(alertTimerRef.current);
    setLocalAlert({ open: true, severity, message });
    alertTimerRef.current = setTimeout(() => {
      setLocalAlert((a) => ({ ...a, open: false }));
      alertTimerRef.current = null;
    }, duration);
  }, []);
  const hideLocal = useCallback(() => {
    if (alertTimerRef.current) {
      clearTimeout(alertTimerRef.current);
      alertTimerRef.current = null;
    }
    setLocalAlert((a) => ({ ...a, open: false }));
  }, []);
  useEffect(
    () => () => {
      if (alertTimerRef.current) clearTimeout(alertTimerRef.current);
    },
    []
  );
  const showSuccess = useCallback((m) => showLocal(m, "success"), [showLocal]);
  const showError = useCallback((m) => showLocal(m, "error", 5000), [showLocal]);

  const [listAnamnez, { isLoading: isLoadingList }] =
    useListAnamnezByPatientMutation();
  const [deleteAnamnez] = useDeleteAnamnezMutation();

  const [list, setList] = useState([]);
  const [initialLoaded, setInitialLoaded] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [creatingNew, setCreatingNew] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const examinationId = initialData?.examinationId;
  const TAB_KEY = "anamnesis";

  // ── Hydrate list ───────────────────────────────────────────
  useEffect(() => {
    if (mode === "session") {
      setList(Array.isArray(initialData?.list) ? initialData.list : []);
      setInitialLoaded(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, initialData?.list]);

  const refresh = useCallback(async () => {
    if (mode !== "standalone" || !token || !patientId) return;
    try {
      const res = await listAnamnez({ token, patientId }).unwrap();
      setList(res?.data?.list || []);
    } catch (_) {
      showError("Anamnez kayıtları yüklenemedi.");
    } finally {
      setInitialLoaded(true);
    }
  }, [mode, token, patientId, listAnamnez, showError]);

  useEffect(() => {
    if (mode === "standalone") refresh();
  }, [mode, refresh]);

  // ── Restore selection from localStorage (session only, on mount) ─
  // Önceden açık bir kayıt varsa onu geri yükle; yoksa varsayılan olarak
  // en güncel (son) anamnez seçili gelsin — muayene sırasında tab açıldığında
  // boş "kayıt seçin" ekranı yerine son anamnez aktif olur.
  useEffect(() => {
    if (mode !== "session" || !examinationId) return;
    if (!initialLoaded || list.length === 0) return;
    const restored = readWorkspaceState(examinationId)?.openRecords?.[TAB_KEY];
    if (restored && list.some((r) => r._id === restored)) {
      setSelectedId(restored);
    } else {
      setSelectedId(list[0]?._id || null);
    }
    // Bilerek list dep'inden hariç: restore sadece ilk hydrate'te yapılmalı.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, examinationId, initialLoaded]);

  // ── Persist selection ──────────────────────────────────────
  useEffect(() => {
    if (mode !== "session" || !examinationId) return;
    writeWorkspaceState(examinationId, (prev) => ({
      ...prev,
      openRecords: { ...(prev.openRecords || {}), [TAB_KEY]: selectedId },
    }));
  }, [mode, examinationId, selectedId]);

  const selected = useMemo(
    () => list.find((r) => r._id === selectedId) || null,
    [list, selectedId]
  );

  const handleSelect = (id) => {
    setCreatingNew(false);
    setSelectedId(id);
  };

  // Yeni kayda geçmeden önceki seçim — vazgeçilince geri dönülür
  const prevSelectedRef = useRef(null);

  const handleNew = () => {
    if (!canEdit) return;
    prevSelectedRef.current = selectedId;
    setSelectedId(null);
    setCreatingNew(true);
  };

  // Kaydetmeden yeni anamnez akışından çık
  const handleCancelNew = () => {
    setCreatingNew(false);
    setSelectedId(prevSelectedRef.current);
  };

  const handleCreated = (created) => {
    setList((prev) => [created, ...prev]);
    setSelectedId(created._id);
    setCreatingNew(false);
    if (typeof onRecordChanged === "function") onRecordChanged();
    showSuccess("Anamnez oluşturuldu.");
  };

  const handleSaved = (updated) => {
    if (!updated || !updated._id) return;
    setList((prev) => prev.map((r) => (r._id === updated._id ? updated : r)));
    showSuccess("Anamnez güncellendi.");
    if (typeof onRecordChanged === "function") onRecordChanged();
  };

  const handleDelete = (rec) => {
    if (!rec?._id) return;
    setPendingDelete(rec);
    setDeleteConfirmOpen(true);
  };

  const confirmDelete = async () => {
    if (!pendingDelete?._id) return;
    setDeleteLoading(true);
    try {
      const promise = deleteAnamnez({
        token,
        patientId,
        anamnezId: pendingDelete._id,
        ...(examinationId ? { examinationId } : {}),
      }).unwrap();
      const wrapped = registerAutoSave ? registerAutoSave(promise) : promise;
      await wrapped;
      setList((prev) => prev.filter((r) => r._id !== pendingDelete._id));
      if (selectedId === pendingDelete._id) setSelectedId(null);
      showSuccess("Anamnez silindi.");
      if (typeof onRecordChanged === "function") onRecordChanged();
    } catch (_) {
      showError("Anamnez silinemedi.");
    } finally {
      setDeleteLoading(false);
      setDeleteConfirmOpen(false);
      setPendingDelete(null);
    }
  };

  const handleEditorError = useCallback(
    (err) => {
      // Sunucunun döndürdüğü spesifik sebebi (eksik/yanlış alan, token vb.) göster.
      const status = err?.status || err?.originalStatus;
      const serverMsg = err?.data?.message;
      if (status === 423) {
        showError(
          serverMsg || `Bu ${terms.patientLower} şu an ${terms.examLoc} — değişiklik kaydedilemedi.`
        );
        return;
      }
      showError(serverMsg || "Anamnez kaydedilemedi.");
    },
    [showError, terms]
  );

  // ── Render ─────────────────────────────────────────────────
  const showLoading = mode === "standalone" && !initialLoaded && isLoadingList;

  // Yeni anamnez oluşturulurken butonu gizle — aktif oluşturma akışı varken
  // tekrar "Yeni Anamnez Oluştur"a basılmasını engelle.
  const headerAction =
    canEdit && !creatingNew ? (
      <ActionButton
        variant="add"
        compact
        palette={palette}
        onClick={handleNew}
        label="Yeni Anamnez Oluştur"
        aria-label="Yeni anamnez oluştur"
      />
    ) : null;

  const showEditor = creatingNew || Boolean(selected);

  return (
    <Box sx={WORKSPACE_ROOT_SX}>
      <WorkspaceBody
        title="Anamnez Kayıtları"
        count={list.length}
        action={headerAction}
        alert={localAlert}
        onAlertClose={hideLocal}
      >
        {showLoading ? (
          <WorkspaceSkeleton />
        ) : list.length === 0 && !creatingNew ? (
          <WorkspaceEmpty
            icon={<NotesIcon />}
            title="Henüz anamnez kaydı yok"
            lead={`${terms.patientGen} ilk anamnez kaydını`}
            tail="Tanı, şikayet, tıbbi geçmiş ve aile öyküsü tek kayıtta toplanır."
            readOnlyText={`Bu ${terms.patientLower} için oluşturulmuş anamnez kaydı bulunmuyor.`}
            onCreate={canEdit ? handleNew : undefined}
            palette={palette}
          />
        ) : (
          <WorkspaceSplit
            list={
              <Stack divider={<Box sx={{ borderBottom: `1px solid ${COLORS.slate100}` }} />}>
                {creatingNew && (
                  <CreatingRow
                    label="Yeni anamnez oluşturuluyor…"
                    hint="Tanı seçilince kaydedilir"
                    onCancel={handleCancelNew}
                    palette={palette}
                  />
                )}
                  {list.map((item) => {
                    const active = item._id === selectedId;
                    return (
                      <SelectableListRow
                        key={item._id}
                        active={active}
                        onClick={() => handleSelect(item._id)}
                        sx={LIST_ROW_SX}
                        title={diagnosisLabel(item.diagnosis)}
                        subtitle={
                          <>
                            {item.complaint && (
                              <RowLine sx={{ mt: 0.25, fontSize: "0.8rem" }}>
                                {item.complaint.length > 60
                                  ? `${item.complaint.slice(0, 60)}…`
                                  : item.complaint}
                              </RowLine>
                            )}
                            <RowEditorLine
                              editor={formatEditor(item.updatedBy || item.createdBy)}
                              rel={formatRelativeTr(item.updatedAt || item.createdAt)}
                            />
                          </>
                        }
                        trailing={
                          <>
                            <RowMetaText>{formatCreatedAt(item.createdAt)}</RowMetaText>
                            {active && canEdit && (
                              <Tooltip title="Anamnezi sil">
                                <IconButton
                                  size="small"
                                  aria-label="Anamnezi sil"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDelete(item);
                                  }}
                                  sx={{
                                    color: COLORS.slate400,
                                    "&:hover": {
                                      color: COLORS.errorDark,
                                      bgcolor: alpha(COLORS.error, 0.08),
                                    },
                                  }}
                                >
                                  <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                                </IconButton>
                              </Tooltip>
                            )}
                          </>
                        }
                      />
                    );
                  })}
              </Stack>
            }
            editor={
              !showEditor ? (
                <SelectPrompt
                  icon={<NotesIcon />}
                  title="Bir anamnez kaydı seçin"
                  description="Detayları görmek ve düzenlemek için soldaki listeden bir kayıt seçin."
                />
              ) : (
                <AnamnesisEditor
                  key={selected?._id || "new"}
                  record={selected}
                  mode={mode}
                  patient={patient}
                  patientId={patientId}
                  token={token}
                  examinationId={examinationId}
                  canEdit={canEdit}
                  onCreated={handleCreated}
                  onSaved={handleSaved}
                  registerAutoSave={registerAutoSave}
                  registerFlush={registerFlush}
                  onSaveStatusChange={onSaveStatusChange}
                  onError={handleEditorError}
                />
              )
            }
          />
        )}
      </WorkspaceBody>

      <ConfirmDialog
        open={deleteConfirmOpen}
        onClose={() => {
          if (deleteLoading) return;
          setDeleteConfirmOpen(false);
          setPendingDelete(null);
        }}
        onConfirm={confirmDelete}
        title="Anamnez kaydını sil"
        message="Bu anamnez kaydı kalıcı olarak silinecek. Devam edilsin mi?"
        confirmText="Sil"
        cancelText="Vazgeç"
        loading={deleteLoading}
      />
    </Box>
  );
}
